/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Container from "../components/Container";
import NavigationHeader from "../components/NavigationHeader";
import PackagesList from "../components/booking/package/PackagesList";
import { usePackages as usePackagesData } from "../lib/react-query/usePackages";
import { useCart } from "../hooks/useCart";
import { FlowType, setFlow } from "../store/slices/flowSlice";
import Button from "../components/ui/Button";
import { CART_LINE_TYPES, ROUTES } from "../constants/constants";
import { StyledFooterStepButton } from "../components/FooterStepButton";
import { useProductsSearchSort } from "../hooks/useProductsSearchSort";
import { useFilterCheckbox } from "../hooks/useFilterCheckbox";

const PACKAGE_FILTER_GROUPS = [
  { type: "service",   label: "Services", extract: (pkg) => pkg.services,        idField: "service_id" },
  { type: "attribute", label: "Add-ons",  extract: (pkg) => pkg.paid_attributes, idField: "product_id" },
  { type: "product",   label: "Products", extract: (pkg) => pkg.products,        idField: "product_id" },
];

const Packages = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { lines, addLine, removeLine, updateLineParams, setEntryRoute } = useCart();
  const { data: packages, isLoading } = usePackagesData();
  const { searchValue, debouncedSearch, sortBy, sortFn, setSearchValue, setSortBy } = useProductsSearchSort();
  const { filterGroups, selectedFilters, toggleFilter, clearFilters, filteredItems } = useFilterCheckbox(packages, PACKAGE_FILTER_GROUPS);

  const filteredPackages = useMemo(() => {
    let result = filteredItems;
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      result = result.filter((pkg) => pkg.name?.toLowerCase().includes(q));
    }
    if (sortFn) result = [...result].sort(sortFn);
    return result;
  }, [filteredItems, debouncedSearch, sortFn]);

  const packageLines = lines.filter((l) => l.type === CART_LINE_TYPES.PACKAGE);

  useEffect(() => {
    dispatch(setFlow(FlowType.CART));
    setEntryRoute(location.pathname);
  }, []);

  const handleSelect = (pkg, startDate) => {
    const existingLine = packageLines.find((l) => l.params.package_id === pkg.id);
    if (existingLine) {
      removeLine({ clientId: existingLine.clientId });
    } else {
      addLine({
        type: CART_LINE_TYPES.PACKAGE,
        entity: pkg,
        params: { package_id: pkg.id, start_date: startDate },
      });
    }
  };

  const handleDateChange = (packageId, startDate) => {
    const line = packageLines.find((l) => l.params.package_id === packageId);
    if (line) {
      updateLineParams({ clientId: line.clientId, patch: { start_date: startDate } });
    }
  };

  return (
    <Container>
      <NavigationHeader title="Packages" clientLogin navigation cart />
      <PackagesList
        packages={filteredPackages}
        isLoading={isLoading}
        selectedPackages={packageLines}
        onSelect={handleSelect}
        onDateChange={handleDateChange}
        searchValue={searchValue}
        sortBy={sortBy}
        onSearchChange={setSearchValue}
        onSortChange={setSortBy}
        filterGroups={filterGroups}
        selectedFilters={selectedFilters}
        onFilterChange={toggleFilter}
        onClearFilters={clearFilters}
      />
      {packageLines.length > 0 && (
        <StyledFooterStepButton>
          <Button
            title="Go to checkout"
            size="l"
            fullWidth
            onClick={() => navigate(ROUTES.ORDER_SUMMARY)}
          />
        </StyledFooterStepButton>
      )}
    </Container>
  );
};

export default Packages;